import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { UserCog, Users, CheckCircle2, Loader2, ShieldCheck, ShieldOff, UserPlus, Trash2, Copy } from "lucide-react";

const EMPTY_FORM = { name: "", email: "", has_paid: false };

export default function AdminUsers() {
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/admin/users").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (ready && user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const setAccess = async (email, has_paid) => {
    setBusy(email);
    try {
      await api.patch(`/admin/users/${encodeURIComponent(email)}/access`, { has_paid });
      toast.success(has_paid ? `Access granted to ${email}` : `Access revoked for ${email}`);
      load();
    } catch {
      toast.error("Could not update access");
    } finally {
      setBusy("");
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/admin/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        has_paid: form.has_paid,
      });
      setAddOpen(false);
      setForm(EMPTY_FORM);
      setCreatedUser({ email: res.data.email, temp_password: res.data.temp_password });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = () => {
    if (!createdUser) return;
    navigator.clipboard.writeText(createdUser.temp_password)
      .then(() => toast.success("Password copied"))
      .catch(() => toast.error("Copy failed — select it manually"));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${encodeURIComponent(deleteTarget.email)}`);
      toast.success("User deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium" });
    } catch {
      return iso;
    }
  };

  const filtered = (data?.users || []).filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });

  const stats = data
    ? [
        { label: "Total Users", value: data.summary.total, icon: Users },
        { label: "Founder", value: data.summary.paid, icon: CheckCircle2 },
      ]
    : [];

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <UserCog className="h-6 w-6 text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Access Control</span>
        </div>
        <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
          <h1 className="font-display text-3xl md:text-4xl font-bold">Users</h1>
          <Button onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }} className="rounded-sm" data-testid="add-user-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Add User
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-8">Grant course access to anyone who paid via the PayPal link — search their email and click <span className="text-primary">Grant access</span>. Add users manually with a one-time temporary password.</p>

        {loading ? (
          <div className="flex items-center justify-center h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {stats.map((s, i) => (
                <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="p-5 rounded-sm border border-border/70 bg-card" data-testid={`user-stat-${i}`}>
                  <s.icon className="h-5 w-5 text-primary mb-4" />
                  <div className="font-mono text-2xl font-bold">{s.value}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>

            <div className="mb-4 max-w-sm">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by email or name…"
                className="bg-secondary/40 border-border h-11" data-testid="user-search" />
            </div>

            <div className="rounded-sm border border-border/70 bg-card overflow-hidden" data-testid="admin-users-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60 hover:bg-transparent">
                    <TableHead className="font-mono text-xs uppercase tracking-widest">User</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Joined</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest">Access</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">No users found.</TableCell></TableRow>
                  ) : (
                    filtered.map((u, i) => (
                      <TableRow key={u.email} className="border-border/50" data-testid={`admin-user-${i}`}>
                        <TableCell>
                          <div className="text-sm font-medium">{u.name}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                          {u.role === "admin" && <span className="text-[10px] font-mono uppercase tracking-widest text-accent">admin</span>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at)}</TableCell>
                        <TableCell>
                          {u.has_paid ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-primary border border-primary/40 bg-primary/10 px-2 py-1 rounded-sm">
                              <ShieldCheck className="h-3.5 w-3.5" /> Founder
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground border border-border bg-secondary/40 px-2 py-1 rounded-sm">
                              <ShieldOff className="h-3.5 w-3.5" /> Free
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {u.has_paid ? (
                              <Button variant="outline" size="sm" className="rounded-sm" disabled={busy === u.email}
                                onClick={() => setAccess(u.email, false)} data-testid={`revoke-access-${i}`}>
                                {busy === u.email ? <Loader2 className="h-4 w-4 animate-spin" /> : "Revoke access"}
                              </Button>
                            ) : (
                              <Button size="sm" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" disabled={busy === u.email}
                                onClick={() => setAccess(u.email, true)} data-testid={`grant-access-${i}`}>
                                {busy === u.email ? <Loader2 className="h-4 w-4 animate-spin" /> : "Grant access"}
                              </Button>
                            )}
                            {u.role === "admin" ? (
                              <Button variant="ghost" size="icon" disabled title="Admin accounts cannot be deleted"
                                className="h-8 w-8 text-muted-foreground/40" data-testid={`delete-user-disabled-${i}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(u)}
                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                data-testid={`delete-user-${i}`} aria-label={`Delete ${u.email}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </main>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>Creates a student account with a one-time temporary password you can share.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addUser} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="u-name">Name *</Label>
              <Input id="u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Pilot" data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-email">Email *</Label>
              <Input id="u-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com" data-testid="user-email-input" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox id="u-paid" checked={form.has_paid} onCheckedChange={(v) => setForm({ ...form, has_paid: !!v })}
                data-testid="user-paid-checkbox" />
              <Label htmlFor="u-paid" className="cursor-pointer">Grant full course access (paid)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="user-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="user-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdUser} onOpenChange={(o) => !o && setCreatedUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>User created</DialogTitle>
            <DialogDescription>
              Share these credentials with <span className="text-foreground font-medium">{createdUser?.email}</span>. This password is shown <span className="text-primary">only once</span> — copy it now.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <div className="font-mono text-sm bg-secondary/40 border border-border rounded-sm px-3 py-2">{createdUser?.email}</div>
            </div>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-sm bg-secondary/40 border border-border rounded-sm px-3 py-2 select-all" data-testid="temp-password-value">
                  {createdUser?.temp_password}
                </div>
                <Button type="button" variant="outline" size="icon" onClick={copyPassword} className="shrink-0" data-testid="copy-password-btn">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedUser(null)} data-testid="temp-password-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="text-foreground font-medium">{deleteTarget?.name}</span> ({deleteTarget?.email}), their learning progress, and any matching contact. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-user-confirm-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
